import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiTrash2, FiPlus, FiMinus, FiLogIn, FiUserPlus } from 'react-icons/fi';
import CloudflareImage from '../components/ui/cloudflare-image';
import { getCart, updateCartItem, removeFromCart, applyCoupon, removeCoupon, applyMadCoins } from '../services/cartService';
import { getAddresses } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import PageLayout from '../components/PageLayout';
import AddressForm from '../components/AddressForm';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const CartContainer = styled.div`
  margin-top: 2rem;
  padding: 0 1rem;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  
  @media (min-width: 768px) {
    padding: 0 5%;
  }
  
  @media (min-width: 1024px) {
    padding: 0 150px;
  }
`;

const CartTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: #111;
  border-bottom: 1px solid #eee;
  padding-bottom: 1rem;
`;

const CartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 640px) {
    max-width: 28rem;
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (min-width: 1024px) {
    max-width: 100%;
    grid-template-columns: 2fr 1fr;
    gap: 2.5rem;
  }
`;

const CartItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  @media (min-width: 768px) {
    grid-template-columns: 120px 1fr;
    gap: 1.5rem;
  }
`;

const ItemImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 4px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ItemTitle = styled.h3`
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  color: #111;
`;

const ItemPrice = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111;
`;

const ItemSize = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid #f0f0f0;
`;

const QuantityButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f5f5f5;
    border-color: #bbb;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityText = styled.span`
  min-width: 24px;
  text-align: center;
  font-size: 0.875rem;
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: none;
  background: none;
  color: #666;
  cursor: pointer;
  font-size: 0.875rem;
  margin-left: auto;
  transition: color 0.2s ease;
  
  &:hover {
    color: #dc2626;
  }
`;

const OrderSummary = styled.div`
  padding: 1.5rem;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  position: sticky;
  top: 180px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SummaryTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #111;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #666;
  
  &:last-of-type {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
    font-size: 1rem;
    font-weight: 600;
    color: #111;
  }
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: #111;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1.5rem;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #000;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin: 2rem 0;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 500;
    margin-bottom: 1rem;
    color: #111;
  }
  
  p {
    color: #666;
    margin-bottom: 2rem;
  }
`;

const ContinueShoppingButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #111;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #000;
  }
`;

const LoginPrompt = styled.div`
  padding: 1.5rem;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  text-align: center;
  position: sticky;
  top: 180px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const PromptTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #111;
`;

const PromptText = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
`;

const AuthButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const AuthButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${props => props.primary ? '#111' : 'transparent'};
  color: ${props => props.primary ? 'white' : '#111'};
  border: ${props => props.primary ? 'none' : '1px solid #111'};
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.primary ? '#000' : '#f5f5f5'};
  }
`;

const AddressSection = styled.div`
  margin-bottom: 2rem;
`;

const AddressCard = styled.div`
  padding: 1rem;
  border: 1px solid ${props => props.selected ? '#000' : '#eee'};
  border-radius: 8px;
  margin-bottom: 1rem;
  cursor: pointer;
`;

const CartPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { refreshCart } = useCart();
  const [cart, setCart] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddressFormOpen, setIsAddressFormOpen] = useState(false);
  const [isCouponModalOpen, setCouponModalOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [isMadCoinsModalOpen, setMadCoinsModalOpen] = useState(false);
  const [madCoinsToUse, setMadCoinsToUse] = useState(0);
  const [madCoinsDiscount, setMadCoinsDiscount] = useState(0);

  const handleApplyCoupon = async () => {
    try {
      const response = await applyCoupon(couponCode);
      if (response.success) {
        const { discountType, discountValue } = response.coupon;
        let calculatedDiscount = 0;
        if (discountType === 'percentage') {
          calculatedDiscount = cart.total * (discountValue / 100);
        } else {
          calculatedDiscount = discountValue;
        }
        // Ensure discount doesn't exceed subtotal
        const finalDiscount = Math.min(calculatedDiscount, cart.total);
        setCouponDiscount(finalDiscount);
        toast.success('Coupon applied successfully');
        setCouponModalOpen(false);
        // Refresh cart to get updated totals
        const cartResponse = await getCart();
        if (cartResponse.success) {
          setCart(cartResponse.data);
          refreshCart(); // Update navbar cart count
        }
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      toast.error('Failed to apply coupon');
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      const response = await removeCoupon();
      if (response.success) {
        setCouponDiscount(0);
        setCouponCode('');
        toast.success('Coupon removed');
        const cartResponse = await getCart();
        if (cartResponse.success) {
          setCart(cartResponse.data);
          refreshCart();
        }
      }
    } catch (error) {
      toast.error('Failed to remove coupon');
    }
  };

  const handleRemoveMadCoins = async () => {
    try {
      const response = await applyMadCoins(0); // Assuming 0 clears it
      if (response.success) {
        setMadCoinsDiscount(0);
        setMadCoinsToUse(0);
        toast.success('MAD Coins removed');
        const cartResponse = await getCart();
        if (cartResponse.success) {
          setCart(cartResponse.data);
          refreshCart();
        }
      }
    } catch (error) {
      toast.error('Failed to remove MAD Coins');
    }
  };

  const handleApplyMadCoins = async () => {
    try {
      if (!madCoinsToUse || madCoinsToUse <= 0) {
        toast.error('Please enter a valid amount of MAD Coins');
        return;
      }

      const availableCoins = Math.round(user?.madCoins?.balance || 0);
      if (madCoinsToUse > availableCoins) {
        toast.error(`You only have ${availableCoins} MAD Coins available`);
        return;
      }

      const response = await applyMadCoins(madCoinsToUse);
      if (response.success) {
        setMadCoinsDiscount(madCoinsToUse); // 1 madcoin = £1
        toast.success(`${madCoinsToUse} MAD Coins applied successfully`);
        setMadCoinsModalOpen(false);
        // Refresh cart to get updated totals
        const cartResponse = await getCart();
        if (cartResponse.success) {
          setCart(cartResponse.data);
          refreshCart(); // Update navbar cart count
        }
      } else {
        toast.error(response.error || 'Failed to apply MAD Coins');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to apply MAD Coins');
    }
  };

  // Fetch cart and address data
  useEffect(() => {
    const fetchCart = async () => {
      try {
        setLoading(true);
        const response = await getCart();
        if (response.success) {
          const cartData = response.data;
          setCart(cartData);

          // Initialize discounts from cart data
          if (cartData.coupon) {
            const { discountType, discountValue } = cartData.coupon;
            let calculatedDiscount = 0;
            if (discountType === 'percentage') {
              calculatedDiscount = cartData.total * (discountValue / 100);
            } else {
              calculatedDiscount = discountValue;
            }
            setCouponDiscount(Math.min(calculatedDiscount, cartData.total));
          } else {
            setCouponDiscount(0);
          }

          if (cartData.madCoinsToUse) {
            setMadCoinsDiscount(cartData.madCoinsToUse);
          } else {
            setMadCoinsDiscount(0);
          }
        } else {
          setError('Failed to load cart');
        }
      } catch (err) {
        console.error('Error fetching cart:', err);
        setError('Failed to load cart');
      } finally {
        setLoading(false);
      }
    };

    const fetchAddresses = async () => {
      if (isAuthenticated()) {
        try {
          const response = await getAddresses();
          if (response.success) {
            setAddresses(response.addresses || []);
            // Set default address as selected
            const defaultAddress = response.addresses.find(a => a.isDefault);
            if (defaultAddress) {
              setSelectedAddress(defaultAddress._id);
            }
          }
        } catch (err) {
          console.error('Failed to fetch addresses:', err);
        }
      }
    };

    fetchCart();
    fetchAddresses();
  }, [isAuthenticated]);

  // Show message when returning from Stripe without completing payment
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('canceled') === 'true') {
      toast.info('Payment was cancelled. Your cart is unchanged. You can try again when you\'re ready.');
      navigate('/cart', { replace: true }); // clear ?canceled=true from URL
    }
  }, [location.search, navigate]);

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setIsAddressFormOpen(true);
  };

  // Handle quantity update
  const handleQuantityChange = async (itemId, currentQuantity, change) => {
    const newQuantity = currentQuantity + change;
    if (newQuantity < 1) return;

    try {
      const response = await updateCartItem(itemId, newQuantity);
      if (response.success) {
        setCart(response.data);
        refreshCart(); // Update navbar cart count
      }
    } catch (err) {
      toast.error(err.toString());
    }
  };

  // Handle item removal
  const handleRemoveItem = async (itemId) => {
    try {
      const response = await removeFromCart(itemId);
      if (response.success) {
        setCart((prevCart) => {
          const newItems = prevCart.items.filter((item) => item._id !== itemId);
          const newTotal = newItems.reduce((acc, item) => acc + item.product.price * item.quantity, 0);
          return {
            ...prevCart,
            items: newItems,
            total: newTotal,
          };
        });
        refreshCart(); // Update navbar cart count
        toast.success('Item removed from cart');
      } else {
        toast.error('Failed to remove item from cart');
      }
    } catch (err) {
      toast.error(err.toString());
    }
  };

  if (loading || !cart) {
    return (
      <PageLayout>
        <CartContainer>
          <div>Loading...</div>
        </CartContainer>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <CartContainer>
          <div>{error}</div>
        </CartContainer>
      </PageLayout>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <PageLayout>
        <CartContainer>
          <EmptyCart>
            <h2>Your cart is empty</h2>
            <p>Add some products to your cart and they will appear here</p>
            <ContinueShoppingButton onClick={() => navigate('/')}>
              Continue Shopping
            </ContinueShoppingButton>
          </EmptyCart>
        </CartContainer>
      </PageLayout>
    );
  }

  const shippingCost = cart.shippingCost ?? 0;
  const totalAfterCoupon = cart.total - couponDiscount;
  const totalAfterMadCoins = Math.max(0, totalAfterCoupon - madCoinsDiscount);
  const grandTotal = Math.round((totalAfterMadCoins + shippingCost) * 100) / 100;

  return (
    <PageLayout>
      <CartContainer>
        <CartTitle>Shopping Cart</CartTitle>
        <CartGrid>
          <CartItems>
            {cart.items.map((item) => (
              <CartItem key={item._id}>
                <ItemImage>
                  <CloudflareImage
                    src={item.product.images.find(img => img.isMain)?.url || item.product.images[0].url}
                    alt={item.product.title}
                  />
                </ItemImage>
                <ItemDetails>
                  <ItemTitle>{item.product.title}</ItemTitle>
                  <ItemPrice>£{item.product.price.toFixed(2)}</ItemPrice>
                  {item.size !== 'OneSize' && <ItemSize>Size: {item.size}</ItemSize>}
                  <QuantityControl>
                    <QuantityButton
                      onClick={() => handleQuantityChange(item._id, item.quantity, -1)}
                      disabled={item.quantity <= 1}
                    >
                      <FiMinus size={14} />
                    </QuantityButton>
                    <QuantityText>{item.quantity}</QuantityText>
                    <QuantityButton
                      onClick={() => handleQuantityChange(item._id, item.quantity, 1)}
                      disabled={item.quantity >= (item.product.sizeVariations?.find(v => v.size === item.size)?.stock || 999)}
                    >
                      <FiPlus size={14} />
                    </QuantityButton>
                    <RemoveButton onClick={() => handleRemoveItem(item._id)}>
                      <FiTrash2 size={14} />
                      Remove
                    </RemoveButton>
                  </QuantityControl>
                </ItemDetails>
              </CartItem>
            ))}
          </CartItems>

          {isAuthenticated() ? (
            <OrderSummary>
              <AddressSection>
                <h2 className="text-xl font-semibold mb-4">Shipping Address</h2>
                {addresses.length > 0 ? (
                  addresses.map(address => (
                    <AddressCard
                      key={address._id}
                      selected={selectedAddress === address._id}
                      onClick={() => handleEditAddress(address)}
                    >
                      <p className="font-bold">{address.addressLine1}</p>
                      <p>{address.townCity}, {address.postcode}</p>
                    </AddressCard>
                  ))
                ) : (
                  <p>No addresses found. Please add one.</p>
                )}
                <Button variant="outline" onClick={() => setIsAddressFormOpen(true)}>Add New Address</Button>
              </AddressSection>
              <hr className="my-4" />
              <SummaryTitle>Order Summary</SummaryTitle>
              <SummaryRow>
                <span>Subtotal</span>
                <span>£{cart.total.toFixed(2)}</span>
              </SummaryRow>
              {couponDiscount > 0 && (
                <SummaryRow>
                  <div className="flex flex-col">
                    <span>Coupon Discount</span>
                    <button onClick={handleRemoveCoupon} className="text-xs text-red-500 text-left hover:underline">Remove</button>
                  </div>
                  <span>- £{couponDiscount.toFixed(2)}</span>
                </SummaryRow>
              )}
              <SummaryRow>
                <span>Total After Coupon</span>
                <span>£{totalAfterCoupon.toFixed(2)}</span>
              </SummaryRow>
              {madCoinsDiscount > 0 && (
                <SummaryRow>
                  <div className="flex flex-col">
                    <span>MAD Coins Discount</span>
                    <button onClick={handleRemoveMadCoins} className="text-xs text-red-500 text-left hover:underline">Remove</button>
                  </div>
                  <span>- £{madCoinsDiscount.toFixed(2)}</span>
                </SummaryRow>
              )}
              <SummaryRow>
                <span>Shipping</span>
                <span>£{shippingCost.toFixed(2)}</span>
              </SummaryRow>
              <SummaryRow>
                <span>Total</span>
                <span>£{grandTotal.toFixed(2)}</span>
              </SummaryRow>
              <div className="text-center text-sm text-gray-500 mt-4">
                <b>You will earn MAD Coins based on your order total after placing the order.</b>
              </div>
              <div className="mt-4 flex gap-2">
                <button className="text-sm text-blue-500" onClick={() => setCouponModalOpen(true)}>Have a Coupon?</button>
                {isAuthenticated() && user?.madCoins?.balance > 0 && (
                  <button className="text-sm text-blue-500" onClick={() => setMadCoinsModalOpen(true)}>
                    Use MAD Coins ({Math.round(user.madCoins.balance)} available)
                  </button>
                )}
              </div>
              <CheckoutButton onClick={() => navigate('/checkout', { state: { selectedAddressId: selectedAddress, cart, couponDiscount } })} disabled={!selectedAddress}>
                Proceed to checkout
              </CheckoutButton>
            </OrderSummary>
          ) : (
            <LoginPrompt>
              <PromptTitle>Please Login or Sign Up</PromptTitle>
              <PromptText>
                To complete your purchase, please login to your account or create a new one.
              </PromptText>
              <AuthButtonsContainer>
                <AuthButton to="/login" primary>
                  <FiLogIn size={16} />
                  Login
                </AuthButton>
                <AuthButton to="/register">
                  <FiUserPlus size={16} />
                  Sign Up
                </AuthButton>
              </AuthButtonsContainer>
            </LoginPrompt>
          )}
        </CartGrid>
      </CartContainer>
      <AddressForm
        isOpen={isAddressFormOpen}
        onClose={() => {
          setIsAddressFormOpen(false);
          setEditingAddress(null);
        }}
        address={editingAddress}
        onSave={(newAddresses) => {
          setAddresses(newAddresses);
          const defaultAddress = newAddresses.find(a => a.isDefault) || newAddresses[0];
          if (defaultAddress) {
            setSelectedAddress(defaultAddress._id);
          }
          setIsAddressFormOpen(false);
          setEditingAddress(null);
        }}
      />

      {/* Coupon Modal */}
      <Dialog open={isCouponModalOpen} onOpenChange={setCouponModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Coupon</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Input
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button onClick={handleApplyCoupon}>Apply</Button>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MAD Coins Modal */}
      <Dialog open={isMadCoinsModalOpen} onOpenChange={setMadCoinsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply MAD Coins</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Available MAD Coins: {Math.round(user?.madCoins?.balance || 0)}</label>
              <p className="text-xs text-gray-500 mb-2">Each MAD Coin = £1 discount when applied</p>
              <Input
                type="number"
                placeholder="Enter amount of MAD Coins to use"
                value={madCoinsToUse || ''}
                onChange={(e) => setMadCoinsToUse(parseInt(e.target.value) || 0)}
                min="0"
                max={Math.round(user?.madCoins?.balance || 0)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleApplyMadCoins}>Apply</Button>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default CartPage;
